import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Chip,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  OutlinedInput,
  LinearProgress,
  Divider,
  Alert,
  Checkbox,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import { 
  Close, 
  Save, 
  Cancel, 
  Add, 
  Delete,
  CheckCircleOutline,
  RadioButtonUnchecked,
  CheckCircle,
  AccessTime,
  Assignment,
  AssignmentTurnedIn,
  AssignmentLate,
  AssignmentReturn,
  History,
} from '@mui/icons-material';
import { Subtask, TaskStatus, SubSubtask, Task, TimeLog } from '../types';
import { useProjectStore } from '../store/useProjectStore';
import { getStatusInfo, formatDate } from '../utils/helpers';
import TimeLogModal from './TimeLogModal';

interface EditSubtaskModalProps {
  open: boolean;
  onClose: () => void;
  task: Task;
  subtask: Subtask;
  allAssignees?: string[];
}

const EditSubtaskModal: React.FC<EditSubtaskModalProps> = ({
  open,
  onClose,
  task,
  subtask,
  allAssignees = []
}) => {
  const { updateSubtask, updateSubtaskStatus, addSubSubtask, toggleSubSubtask, deleteSubSubtask, addTimeLog, updateTimeLog, deleteTimeLog } = useProjectStore();
  const [editedData, setEditedData] = useState({
    text: '',
    assignees: [] as string[],
    startDate: '',
    expectedDate: '',
    status: '' as TaskStatus,
  });
  const [assigneeInput, setAssigneeInput] = useState('');
  const [newSubSubtaskText, setNewSubSubtaskText] = useState('');
  const [showAddSubSubtask, setShowAddSubSubtask] = useState(false);
  const [showTimeLogModal, setShowTimeLogModal] = useState(false);
  const [selectedSubSubtaskId, setSelectedSubSubtaskId] = useState<string | null>(null);

  useEffect(() => {
    if (subtask && open) {
      setEditedData({
        text: subtask.text,
        assignees: subtask.assignees || [],
        startDate: subtask.startDate ? new Date(subtask.startDate).toISOString().split('T')[0] : '',
        expectedDate: subtask.expectedDate ? new Date(subtask.expectedDate).toISOString().split('T')[0] : '',
        status: subtask.status,
      });
    }
  }, [subtask, open]);

  const handleSave = () => {
    // Update subtask data
    updateSubtask(task.id, subtask.id, {
      text: editedData.text,
      assignees: editedData.assignees,
      startDate: editedData.startDate ? new Date(editedData.startDate) : undefined,
      expectedDate: editedData.expectedDate ? new Date(editedData.expectedDate) : undefined,
    });

    // Update status separately if changed
    if (editedData.status !== subtask.status) {
      updateSubtaskStatus(task.id, subtask.id, editedData.status);
    }

    onClose();
  };

  const handleAddSubSubtask = () => {
    if (newSubSubtaskText.trim()) {
      addSubSubtask(task.id, subtask.id, newSubSubtaskText);
      setNewSubSubtaskText('');
      setShowAddSubSubtask(false);
    }
  };

  const handleToggleSubSubtask = (subSubtaskId: string) => {
    toggleSubSubtask(task.id, subtask.id, subSubtaskId);
  };

  const handleDeleteSubSubtask = (subSubtaskId: string) => {
    if (window.confirm('確定要刪除此細項嗎？')) {
      deleteSubSubtask(task.id, subtask.id, subSubtaskId);
    }
  };

  const handleTimeLogClick = (subSubtaskId?: string) => {
    setSelectedSubSubtaskId(subSubtaskId || null);
    setShowTimeLogModal(true);
  };

  const handleAddTimeLog = (timeLog: Omit<TimeLog, 'id' | 'createdAt'>) => {
    addTimeLog(task.id, subtask.id, selectedSubSubtaskId, timeLog);
  };

  const handleUpdateTimeLog = (timeLogId: string, updates: Partial<TimeLog>) => {
    updateTimeLog(task.id, subtask.id, selectedSubSubtaskId, timeLogId, updates);
  };

  const handleDeleteTimeLog = (timeLogId: string) => {
    deleteTimeLog(task.id, subtask.id, selectedSubSubtaskId, timeLogId);
  };

  const getCurrentTimeLogs = () => {
    if (selectedSubSubtaskId) {
      const subSubtask = subtask.subSubtasks.find(sst => sst.id === selectedSubSubtaskId);
      return subSubtask?.timeLogs || [];
    }
    return subtask.timeLogs || [];
  };

  const statusOptions: { value: TaskStatus; label: string }[] = [
    { value: 'pending', label: '待執行' },
    { value: 'in-progress', label: '執行中' },
    { value: 'pending-review', label: '待驗收' },
    { value: 'reviewing', label: '驗收中' },
    { value: 'review-failed', label: '驗收失敗' },
    { value: 'completed', label: '已完成' },
  ];

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">編輯子任務</Typography>
            <Chip 
              label={subtask.role} 
              size="small" 
              sx={{ 
                bgcolor: 'primary.main', 
                color: 'white',
                fontWeight: 600 
              }} 
            />
            <IconButton
              size="small"
              onClick={() => handleTimeLogClick()}
              title="子任務工時記錄"
              sx={{ 
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' }
              }}
            >
              <AccessTime />
              {subtask.timeLogs?.length > 0 && (
                <Chip
                  label={subtask.timeLogs.length}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    fontSize: '0.6rem',
                    height: 16,
                    minWidth: 16,
                  }}
                />
              )}
            </IconButton>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Task Description */}
            <TextField
              fullWidth
              label="任務描述"
              value={editedData.text}
              onChange={(e) => setEditedData({ ...editedData, text: e.target.value })}
              required
              multiline
              rows={2}
            />

            {/* Status Selection */}
            <FormControl fullWidth>
              <InputLabel>狀態</InputLabel>
              <Select
                value={editedData.status}
                label="狀態"
                onChange={(e) => setEditedData({ ...editedData, status: e.target.value as TaskStatus })}
              >
                {statusOptions.map((option) => {
                  const statusInfo = getStatusInfo(option.value);
                  return (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: statusInfo.color,
                            color: statusInfo.textColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                          }}
                        >
                          {statusInfo.icon}
                        </Box>
                        <Typography>{option.label}</Typography>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* Assignees */}
            <Autocomplete
              multiple
              freeSolo
              options={allAssignees}
              value={editedData.assignees}
              onChange={(_, newValue) => setEditedData({ ...editedData, assignees: newValue })}
              inputValue={assigneeInput}
              onInputChange={(_, newInputValue) => setAssigneeInput(newInputValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    avatar={
                      <Box sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}>
                        {option.charAt(0)}
                      </Box>
                    }
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="負責人員"
                  placeholder="輸入名稱或選擇現有人員"
                  helperText="可輸入多個負責人，按 Enter 確認"
                />
              )}
            />

            {/* Date Fields */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                sx={{ flex: 1 }}
                type="date"
                label="開始日期"
                InputLabelProps={{ shrink: true }}
                value={editedData.startDate}
                onChange={(e) => setEditedData({ ...editedData, startDate: e.target.value })}
              />
              <TextField
                sx={{ flex: 1 }}
                type="date"
                label="預計完成日期"
                InputLabelProps={{ shrink: true }}
                value={editedData.expectedDate}
                onChange={(e) => setEditedData({ ...editedData, expectedDate: e.target.value })}
                required
              />
            </Box>

            {/* Sub-subtasks Management */}
            <Box sx={{ 
              bgcolor: 'grey.50', 
              p: 2, 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  細項任務管理
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setShowAddSubSubtask(true)}
                  variant="outlined"
                >
                  新增細項
                </Button>
              </Box>

              {/* Sub-subtasks List */}
              {subtask.subSubtasks && subtask.subSubtasks.length > 0 ? (
                <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, mb: 1 }}>
                  {subtask.subSubtasks.map((subSubtask) => (
                    <ListItem key={subSubtask.id} divider>
                      <ListItemIcon>
                        <IconButton 
                          edge="start" 
                          size="small"
                          onClick={() => handleToggleSubSubtask(subSubtask.id)}
                        >
                          {subSubtask.completed ? (
                            <CheckCircle color="success" />
                          ) : (
                            <RadioButtonUnchecked />
                          )}
                        </IconButton>
                      </ListItemIcon>
                      <ListItemText 
                        primary={subSubtask.text}
                        sx={{
                          textDecoration: subSubtask.completed ? 'line-through' : 'none',
                          color: subSubtask.completed ? 'text.secondary' : 'text.primary',
                        }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleTimeLogClick(subSubtask.id)}
                        >
                          <AccessTime fontSize="small" />
                          {subSubtask.timeLogs?.length > 0 && (
                            <Typography variant="caption" sx={{ ml: 0.5 }}>
                              {subSubtask.timeLogs.length}
                            </Typography>
                          )}
                        </IconButton>
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleDeleteSubSubtask(subSubtask.id)}
                        >
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  尚無細項任務
                </Typography>
              )}

              {/* Add Sub-subtask Input */}
              {showAddSubSubtask && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="輸入細項描述..."
                    value={newSubSubtaskText}
                    onChange={(e) => setNewSubSubtaskText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddSubSubtask();
                      }
                    }}
                    autoFocus
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleAddSubSubtask}
                  >
                    新增
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setShowAddSubSubtask(false);
                      setNewSubSubtaskText('');
                    }}
                  >
                    取消
                  </Button>
                </Box>
              )}

              {/* Progress Info */}
              {subtask.subSubtasks && subtask.subSubtasks.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                  <Chip 
                    label={`${subtask.subSubtasks.filter(s => s.completed).length}/${subtask.subSubtasks.length} 已完成`}
                    size="small"
                    color={subtask.subSubtasks.every(s => s.completed) ? 'success' : 'default'}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {Math.round((subtask.subSubtasks.filter(s => s.completed).length / subtask.subSubtasks.length) * 100)}% 完成
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Time Summary Section */}
            <Box sx={{ 
              bgcolor: 'primary.lighter', 
              p: 2, 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'primary.light'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccessTime color="primary" />
                <Typography variant="subtitle2" fontWeight={600}>
                  工時統計
                </Typography>
              </Box>
              
              {/* Subtask Time Summary */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    子任務總工時
                  </Typography>
                  <Chip 
                    label={`${subtask.timeLogs?.reduce((sum, log) => sum + log.hours, 0) || 0} 小時`}
                    size="small"
                    color="primary"
                  />
                </Box>
                {subtask.timeLogs && subtask.timeLogs.length > 0 && (
                  <Box sx={{ pl: 2 }}>
                    {Object.entries(
                      subtask.timeLogs.reduce((acc, log) => {
                        acc[log.assignee] = (acc[log.assignee] || 0) + log.hours;
                        return acc;
                      }, {} as Record<string, number>)
                    ).map(([assignee, hours]) => (
                      <Box key={assignee} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {assignee}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {hours.toFixed(1)} 小時
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              
              {/* Sub-subtasks Time Summary */}
              {subtask.subSubtasks && subtask.subSubtasks.length > 0 && (
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    細項工時分布
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {subtask.subSubtasks.map((subSubtask) => {
                      const subSubtaskHours = subSubtask.timeLogs?.reduce((sum, log) => sum + log.hours, 0) || 0;
                      if (subSubtaskHours === 0) return null;
                      
                      return (
                        <Box key={subSubtask.id} sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ 
                              maxWidth: '60%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {subSubtask.text}
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                              {subSubtaskHours.toFixed(1)} 小時
                            </Typography>
                          </Box>
                          {/* Show assignee breakdown for sub-subtask */}
                          {subSubtask.timeLogs && subSubtask.timeLogs.length > 1 && (
                            <Box sx={{ pl: 2, mt: 0.5 }}>
                              {Object.entries(
                                subSubtask.timeLogs.reduce((acc, log) => {
                                  acc[log.assignee] = (acc[log.assignee] || 0) + log.hours;
                                  return acc;
                                }, {} as Record<string, number>)
                              ).map(([assignee, hours]) => (
                                <Typography key={assignee} variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                  • {assignee}: {hours.toFixed(1)}h
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
              
              {/* Total Time */}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" fontWeight={600}>
                  總計工時
                </Typography>
                <Chip 
                  label={`${
                    (subtask.timeLogs?.reduce((sum, log) => sum + log.hours, 0) || 0) +
                    (subtask.subSubtasks?.reduce((sum, sst) => 
                      sum + (sst.timeLogs?.reduce((ssum, log) => ssum + log.hours, 0) || 0), 0
                    ) || 0)
                  } 小時`}
                  color="primary"
                  variant="filled"
                />
              </Box>
            </Box>

            {/* Original Dates Info */}
            <Box sx={{ 
              bgcolor: 'info.lighter', 
              p: 2, 
              borderRadius: 1,
              display: 'flex',
              gap: 3
            }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  原始開始日期
                </Typography>
                <Typography variant="body2">
                  {subtask.startDate ? formatDate(subtask.startDate) : '未設定'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  原始預計完成
                </Typography>
                <Typography variant="body2">
                  {subtask.expectedDate ? formatDate(subtask.expectedDate) : '未設定'}
                </Typography>
              </Box>
              {subtask.actualCompletedDate && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    實際完成日期
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    {formatDate(subtask.actualCompletedDate)}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Review History Timeline */}
            {subtask.reviewHistory && subtask.reviewHistory.length > 0 && (
              <Box sx={{ 
                bgcolor: 'background.paper', 
                p: 2, 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <History color="action" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    審核歷史記錄
                  </Typography>
                  <Chip 
                    label={`第 ${subtask.reviewHistory.length} 次審核`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>

                <Timeline sx={{ m: 0, p: 0 }}>
                  {subtask.reviewHistory.map((review, index) => {
                    const isLatest = index === subtask.reviewHistory.length - 1;
                    const isFirstReview = review.reviewCount === 1;
                    const isResubmission = review.reviewCount > 1 && !review.passed;
                    
                    const getReviewIcon = () => {
                      if (review.passed) {
                        return <AssignmentTurnedIn />;
                      } else if (isFirstReview) {
                        return <Assignment />;
                      } else {
                        return <AssignmentLate />;
                      }
                    };

                    const getReviewColor = () => {
                      if (review.passed) {
                        return 'success';
                      } else if (isResubmission) {
                        return 'error';
                      } else {
                        return 'primary';
                      }
                    };

                    const getActionLabel = () => {
                      if (review.passed) {
                        return `第 ${review.reviewCount} 次審核通過`;
                      } else if (review.reviewCount === 1) {
                        return '首次提交審核';
                      } else {
                        return `第 ${review.reviewCount} 次審核失敗`;
                      }
                    };

                    return (
                      <TimelineItem key={review.id} sx={{ 
                        minHeight: 'auto',
                        '&::before': { display: 'none' }
                      }}>
                        <TimelineOppositeContent sx={{ 
                          flex: 0.3, 
                          px: 1, 
                          py: 0.5,
                          minWidth: 100
                        }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(review.date)}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {new Date(review.date).toLocaleTimeString('zh-TW', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </Typography>
                        </TimelineOppositeContent>
                        <TimelineSeparator>
                          <TimelineConnector sx={{ 
                            bgcolor: index === 0 ? 'transparent' : 'grey.300',
                            visibility: index === 0 ? 'hidden' : 'visible'
                          }} />
                          <TimelineDot 
                            color={getReviewColor() as any}
                            variant={isLatest ? 'filled' : 'outlined'}
                          >
                            {getReviewIcon()}
                          </TimelineDot>
                          <TimelineConnector sx={{ 
                            bgcolor: index === subtask.reviewHistory.length - 1 ? 'transparent' : 'grey.300',
                            visibility: index === subtask.reviewHistory.length - 1 ? 'hidden' : 'visible'
                          }} />
                        </TimelineSeparator>
                        <TimelineContent sx={{ pb: 2, px: 2 }}>
                          <Box sx={{ 
                            bgcolor: isLatest ? 'action.hover' : 'transparent',
                            p: isLatest ? 1.5 : 0,
                            borderRadius: 1,
                            border: isLatest ? '1px solid' : 'none',
                            borderColor: 'divider'
                          }}>
                            <Typography variant="body2" fontWeight={isLatest ? 600 : 500}>
                              {getActionLabel()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              審核次數：第 {review.reviewCount} 次
                            </Typography>
                            {review.reason && (
                              <Box sx={{ 
                                mt: 1, 
                                p: 1, 
                                bgcolor: 'grey.50', 
                                borderRadius: 0.5,
                                borderLeft: '3px solid',
                                borderLeftColor: !review.passed ? 'error.main' : 'success.main'
                              }}>
                                <Typography variant="caption" color="text.secondary">
                                  {review.passed ? '通過原因' : '失敗原因'}：{review.reason}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TimelineContent>
                      </TimelineItem>
                    );
                  })}
                </Timeline>

                {/* Review Status Alert */}
                {editedData.status === 'review-failed' && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      任務審核失敗，請根據審核意見修改後重新提交
                    </Typography>
                  </Alert>
                )}
                
                {editedData.status === 'pending-review' && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      任務已提交審核，等待主管確認
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}

            {/* Status Flow Hint */}
            {(editedData.status === 'review-failed' || editedData.status === 'pending-review' || editedData.status === 'reviewing') && (
              <Box sx={{ 
                bgcolor: 'grey.50', 
                p: 2, 
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'grey.400'
              }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  審核流程提示：
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  <Chip label="執行中" size="small" />
                  <Typography variant="caption">→</Typography>
                  <Chip label="待驗收" size="small" color="info" />
                  <Typography variant="caption">→</Typography>
                  <Chip label="驗收中" size="small" color="warning" />
                  <Typography variant="caption">→</Typography>
                  {editedData.status === 'review-failed' ? (
                    <>
                      <Chip label="驗收失敗" size="small" color="error" />
                      <Typography variant="caption">→</Typography>
                      <Chip label="執行中（修改）" size="small" />
                      <Typography variant="caption">→</Typography>
                      <Chip label="待驗收（重新提交）" size="small" color="info" variant="outlined" />
                    </>
                  ) : (
                    <Chip label="已完成" size="small" color="success" />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
          <Button 
            onClick={onClose}
            startIcon={<Cancel />}
          >
            取消
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            startIcon={<Save />}
            disabled={!editedData.text || !editedData.expectedDate}
          >
            儲存變更
          </Button>
        </DialogActions>
      </Dialog>

      {/* 工時記錄 Modal */}
      <TimeLogModal
        open={showTimeLogModal}
        onClose={() => setShowTimeLogModal(false)}
        taskTitle={task.title}
        subtaskText={subtask.text}
        subSubtaskText={selectedSubSubtaskId ? 
          subtask.subSubtasks.find(sst => sst.id === selectedSubSubtaskId)?.text : 
          undefined
        }
        assignees={editedData.assignees}
        timeLogs={getCurrentTimeLogs()}
        onAddTimeLog={handleAddTimeLog}
        onUpdateTimeLog={handleUpdateTimeLog}
        onDeleteTimeLog={handleDeleteTimeLog}
      />
    </>
  );
};

export default EditSubtaskModal; 